// Copyright (C) 2023 Cochise Ruhulessin
//
// All rights reserved. No warranty, explicit or implicit, provided. In
// no event shall the author(s) be liable for any claim or damages.
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,

import { ProcurementClient } from "../api"

// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
interface SupplierDescriptor {
  id: number
  name: string
  email: string
}


interface PurchaseOrderProductDTO {
  name: string
  sku: string | null
  quantity: number
  price: string
  grade?: string | null
}

interface PurchaseOrderDTO {
  buyer: string
  currency: string
  expected_delivery: string
  incoterms: string
  port_of_delivery: any
  supplier: SupplierDescriptor
  lines: PurchaseOrderProductDTO[]
}


class PurchaseOrderProduct {
  private data: PurchaseOrderProductDTO
  private index: number
  private order: PurchaseOrder

  constructor(order: PurchaseOrder, index: number, data: PurchaseOrderProductDTO) {
    this.data = data
    this.index = index
    this.order = order
  }

  asDataTransferObject(): PurchaseOrderProductDTO { return this.data }
  getGrade(): string | null { return this.data.grade || null }
  getName(): string { return this.data.name }
  getPrice(): number { return parseFloat(this.data.price) }
  getQuantity(): number { return this.data.quantity }
  getSku(): string | null { return this.data.sku }
  update(dto: PurchaseOrderProductDTO): void {
    this.data = {...this.data, ...dto}
    this.order.changeLine(this.index, dto)
  }
}


class PurchaseOrder {
  private client: ProcurementClient
  private setState: (value: PurchaseOrderDTO) => void
  private data: PurchaseOrderDTO

  static clear(): void {
    localStorage.removeItem("PurchaseOrder")
  }

  static restore(): PurchaseOrderDTO {
    return JSON.parse(localStorage.getItem("PurchaseOrder") || "{}")
  }

  constructor(client: ProcurementClient, state: PurchaseOrderDTO, setState: (value: PurchaseOrderDTO) => void) {
    this.client = client
    this.setState = setState
    this.data = state
    if (!this.data.lines) {
      this.data.lines = []
    }
    if (!this.data.incoterms) {
      this.data.incoterms = "EXW"
    }
    if (!this.data.port_of_delivery) {
      this.data.port_of_delivery = {
        "address1": "Hendrik Figeeweg 1/N",
        "address2": "",
        "address3": "",
        "postal_code": "2031 BJ",
        "city": "Haarlem",
        "region": "",
        "country": "NL",
        "label": "Hendrik Figeeweg 1/N, 2031 BJ, Haarlem, The Netherlands"
      }
    }
  }

  asDataTransferObject(): PurchaseOrderDTO {
    return this.data
  }

  addLine(line: PurchaseOrderProductDTO): void {
    this.data.lines.unshift(line)
    this.persist({})
  }

  changeLine(index: number, dto: PurchaseOrderProductDTO): void {
    this.data.lines[index] = dto
    this.persist({})
  }

  removeLine(index: number): void {
    this.data.lines = this.data.lines.filter((v: any, n: number) => index !== n)
    this.persist({})
  }

  getBuyerName(): string | null {
    return this.data.buyer || null
  }

  getCurrency(): string {
    return this.data.currency
  }

  getAddressFormatted(): string | null {
    return (!this.data.port_of_delivery) ? null : (
      `${this.data.port_of_delivery.address1}, `
      + `${this.data.port_of_delivery.postal_code}, ${this.data.port_of_delivery.city}, `
      + `${this.data.port_of_delivery.country}`
    )
  }

  getLocation() {
    return this.data.port_of_delivery
  }

  getProducts(): PurchaseOrderProduct[] {
    return this.data.lines.map((dto, n) => {
      return new PurchaseOrderProduct(this, n, dto)
    })
  }

  getSupplierName(): string | null {
    return (this.data.supplier) ? this.data.supplier.name : null
  }

  isValid(): boolean {
    return !!this.data.buyer
      && !!this.data.currency
      && !!this.data.incoterms
      && !!this.data.port_of_delivery
      && !!this.data.supplier
      && !!this.data.expected_delivery
  }

  async place(): Promise<void> {
    await this.client.place(this.asDataTransferObject())
  }

  public persist(data: any): void {
    this.data = {...this.data, ...data}
    localStorage.setItem("PurchaseOrder", JSON.stringify(this.data))
    this.setState({...this.data, ...data})
  }
}

export {PurchaseOrder, PurchaseOrderProduct}
export type {PurchaseOrderDTO, PurchaseOrderProductDTO}